import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import { exportWorkbookSheets } from "../helpers/exportWorkbook";

function formatCount(value) {
  return new Intl.NumberFormat("en-GB").format(Number(value || 0));
}

export function buildCriteriaRows(buckets, labelKey = "label") {
  return (buckets || []).map((bucket, index) => ({
    id: `criteria-row-${index}`,
    [labelKey]: bucket?.label ?? bucket?.key ?? bucket?.value ?? "Unknown",
    count: Number(bucket?.count || 0),
  }));
}

export function buildBucketRows(buckets) {
  return buildCriteriaRows(
    (buckets || []).map((bucket) => ({
      label: bucket?.value ?? bucket?.key ?? bucket?.label ?? "Unknown",
      count: bucket?.count,
    }))
  );
}

export function createCountColumns(label, countLabel = "Companies") {
  return [
    { key: "label", label },
    {
      key: "count",
      label: countLabel,
      align: "right",
      format: (value) => formatCount(value),
    },
  ];
}

function CriteriaExtractionTable({ columns, rows, emptyLabel, minHeight = 0, maxHeight = 320 }) {
  if (!rows.length) {
    return (
      <Box
        sx={{
          minHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          color: "#64748b",
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.9rem" }}>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight,
        maxHeight,
        borderRadius: 2,
        borderColor: "rgba(15, 23, 42, 0.08)",
        overflowX: "auto",
        overflowY: "auto",
      }}
    >
      <Table stickyHeader size="small" sx={{ width: "max-content", minWidth: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align || "left"}
                sx={{
                  backgroundColor: "#f8fafc",
                  fontWeight: 700,
                  whiteSpace: column.noWrap ? "nowrap" : "normal",
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow hover key={row.id}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{ whiteSpace: column.noWrap ? "nowrap" : "normal" }}
                >
                  {column.format ? column.format(row[column.key], row) : row[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CriteriaExtractionSection({
  title,
  subtitle,
  columns,
  rows,
  expanded,
  onChange,
  emptyLabel,
  minHeight,
  maxHeight,
}) {
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, nextExpanded) => onChange(nextExpanded)}
      disableGutters
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: "14px !important",
        backgroundColor: "#ffffff",
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{
          px: 1.5,
          minHeight: 52,
          "& .MuiAccordionSummary-content": {
            my: 1,
          },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{subtitle}</Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0, minWidth: 0 }}>
        <CriteriaExtractionTable
          columns={columns}
          rows={rows}
          emptyLabel={emptyLabel}
          minHeight={minHeight}
          maxHeight={maxHeight}
        />
      </AccordionDetails>
    </Accordion>
  );
}

function rowsForWorkbook(section) {
  return (section.rows || []).map((row) =>
    (section.columns || []).reduce((acc, column) => {
      acc[column.label] = row[column.key];
      return acc;
    }, {})
  );
}

export default function CriteriaExtractionPanel({
  title,
  subtitle,
  summaryText,
  sections,
  defaultFileName,
}) {
  const [expanded, setExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [fileName, setFileName] = useState(defaultFileName || "criteria_extraction");

  const workbookSections = useMemo(
    () =>
      (sections || []).filter(
        (section) => Array.isArray(section.rows) && section.rows.length > 0
      ),
    [sections]
  );

  const downloadWorkbook = () => {
    const sheets = workbookSections.map((section) => ({
      sheetName: section.sheetName || section.title,
      rows: rowsForWorkbook(section),
    }));

    exportWorkbookSheets(
      sheets.length > 0 ? sheets : [{ sheetName: "Criteria Extraction", rows: [] }],
      fileName || defaultFileName || "criteria_extraction"
    );
    setDownloadDialogOpen(false);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
        disableGutters
        sx={{
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: "18px !important",
          backgroundColor: "#ffffff",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRoundedIcon />}
          sx={{
            px: 1.5,
            minHeight: 56,
            "& .MuiAccordionSummary-content": {
              my: 1,
            },
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
            {subtitle ? (
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setDownloadDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 999,
              }}
            >
              Download extraction workbook
            </Button>
            {summaryText ? (
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                {summaryText}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: "grid", gap: 1.25, minWidth: 0, width: "100%", maxWidth: "100%" }}>
            {(sections || []).map((section) => (
              <CriteriaExtractionSection
                key={section.id}
                title={section.title}
                subtitle={section.subtitle}
                columns={section.columns}
                rows={section.rows || []}
                expanded={Boolean(expandedSections[section.id])}
                onChange={(nextExpanded) =>
                  setExpandedSections((current) => ({
                    ...current,
                    [section.id]: nextExpanded,
                  }))
                }
                emptyLabel={section.emptyLabel}
                minHeight={section.minHeight ?? 0}
                maxHeight={section.maxHeight ?? 320}
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Download Extraction Workbook</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Workbook Name"
              value={fileName}
              onChange={(event) => setFileName(event.target.value)}
              helperText="Downloads as an .xlsx workbook."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FileDownloadOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDownloadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={downloadWorkbook}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
